<template>
  <div>
    <!-- Two Vuetify-2 leftovers were shrinking the usable text area here:

         1. `:prominent` is NOT a Vuetify 3 prop, so it silently did nothing and
            a two-line notification never got its taller bar. V3's nearest
            equivalent, `density="prominent"`, DOUBLES the height (VToolbar's
            contentHeight = height + height = 128px) — faithful to V2, but far
            more than two lines of this text need, and it also bottom-aligns the
            title and bumps it to 1.5rem. So the height is set directly instead;
            see `barHeight`.
         2. The two `<v-spacer>`s. V2's `.v-toolbar__title` was `flex: 0 1 auto`,
            so it sized to its content and the spacers merely centred it. V3
            made it `flex: 1 1` with `min-width: 0`, so the title COMPETED with
            both spacers for width and ended up with roughly a third of the bar
            — then ellipsised whatever did not fit. Dropping the spacers gives
            the title the full width; `text-center` (in getBarTextClass) still
            centres the text inside it, and the `pl-9` there offsets the close
            button so it stays optically centred.

         `dark` is likewise dead in V3 (removed in favour of `theme`) and is
         gone; the text colours are already set explicitly per notification type
         by getBarTextClass/getBarIconClass. -->
    <v-app-bar
      :color="getBarColor()"
      flat
      density="compact"
      :height="barHeight"
    >
      <v-toolbar-title :class="getBarTextClass()">
        <v-icon v-if="!!getBarIcon()" :class="getBarIconClass()">{{
          getBarIcon()
        }}</v-icon>
        {{ messageLine1 }}
        <div :class="getLine2Class()">{{ messageLine2 }}</div>
      </v-toolbar-title>
      <v-btn icon @click="removeNotification">
        <v-icon>mdi-close-circle</v-icon>
      </v-btn>
    </v-app-bar>
  </div>
</template>

<script>
/* eslint-disable no-console */
// Pinia's mapActions, not Vuex's — the notification module has moved. See the
// note in NotificationContainer.vue.
import { mapActions } from 'pinia'
import { useNotificationStore } from '~/stores/notification'

export default {
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    mn: '[components/defaultLayout/NotificationBar.vue]',
  }),
  computed: {
    message() {
      return this.notification.statusCode
        ? `${this.notification.message} (${this.notification.statusCode})`
        : this.notification.message
    },
    messageLines() {
      return this.message.split('<br>')
    },
    messageLine1() {
      return this.messageLines.length > 0 ? this.messageLines[0] : ''
    },
    messageLine2() {
      return this.messageLines.length > 1 ? this.messageLines[1] : ''
    },
    hasTwoMessageLines() {
      return this.messageLines.length === 2
    },
    /**
     * Bar height. VToolbar's contentHeight subtracts 16 at density="compact",
     * so these render as 48px (one line, unchanged) and 72px (two lines).
     * Tune the 88 alone -- nothing else depends on it.
     */
    barHeight() {
      return this.hasTwoMessageLines ? 88 : 64
    },
    doAutoRemove() {
      return (
        this.notification.doAutoRemove !== 'undefined' &&
        this.notification.doAutoRemove === 'true'
      )
    },
    removeMessagePrefix() {
      return this.doAutoRemove ? 'AUTO REMOVE' : 'BUTTON CLICKED'
    },
  },
  mounted() {
    if (this.doAutoRemove) {
      setTimeout(() => {
        this.removeNotification()
      }, 5000)
    }
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  },
  methods: {
    ...mapActions(useNotificationStore, ['remove']),
    removeNotification() {
      const fn = `${this.mn} removeNotification |`
      console.debug(`${fn} ${this.removeMessagePrefix}: REMOVE NOTIFICATION`)
      this.remove(this.notification)
    },
    getBarColor() {
      const fn = `${this.mn} getBarColor |`
      console.debug(`${fn} this.notification:`, this.notification)
      if (this.notification.type === 'error') {
        return 'error-lighten-1'
      } else if (this.notification.type === 'warning') {
        return 'warning'
      } else {
        return 'info'
      }
    },
    getBarIcon() {
      const fn = `${this.mn} getBarIcon |`
      console.debug(`${fn} this.notification:`, this.notification)
      if (this.notification.type === 'error') {
        return 'mdi-alert-circle-outline'
      } else if (this.notification.type === 'warning') {
        return 'mdi-alert-outline'
      } else {
        return 'mdi-information-outline'
      }
    },
    getBarIconClass() {
      const barTextClassString =
        'mt-n1 mr-1' +
        ' ' +
        (this.notification.type === 'warning' ||
        this.notification.type === 'error'
          ? 'text-grey-darken-4'
          : 'text-white')

      return barTextClassString
    },
    getBarTextClass() {
      const barTextClassString =
        'pl-9' +
        ' ' +
        'text-center text-subtitle-1 font-weight-medium' +
        ' ' +
        (this.notification.type === 'warning' ||
        this.notification.type === 'error'
          ? 'text-grey-darken-4'
          : 'text-white')

      return barTextClassString
    },
    getLine2Class() {
      return this.hasTwoMessageLines ? 'mt-n2' : ''
    },
  },
}
</script>
